import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { supabase } from "@/shared/lib/supabase";
import { decryptPersonData } from "@/shared/utils/serverEncryption";
import { useAppContext } from "@/shared/hooks/useAppContext";

const SSN_STALE_TIME_MS = 5 * 60 * 1000;

interface UseDeceasedSSNDataResult {
  deceasedSSN: string | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<UseQueryResult<string | null, Error>>;
}

/**
 * Hook for retrieving and decrypting deceased person's SSN from persons table
 * Uses server-side encryption for secure decryption
 */
export function useDeceasedSSNData(): UseDeceasedSSNDataResult {
  const { person } = useAppContext();

  const {
    data: deceasedSSN,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["deceasedSSNData", person?.estate_id],
    queryFn: async () => {
      if (!person?.estate_id) {
        return null;
      }

      // Fetch encrypted SSN directly from database
      const { data: personData, error } = await supabase
        .from("estates")
        .select("ssn_encrypted")
        .eq("estate_id", person.estate_id)
        .single();

      if (error || !personData?.ssn_encrypted) {
        return null;
      }

      try {
        const decryptedSSN = await decryptPersonData(
          person.estate_id,
          personData.ssn_encrypted,
          "ssn",
        );

        return decryptedSSN;
      } catch (error) {
        console.error("Server-side decryption failed:", error);
        throw new Error("Failed to decrypt deceased SSN");
      }
    },
    enabled: !!person?.estate_id,
    retry: 1,
    staleTime: (query) => (query.state.data ? SSN_STALE_TIME_MS : 0),
  });

  return {
    deceasedSSN: deceasedSSN || null,
    isLoading,
    error: error?.message || null,
    refetch,
  };
}
